import { NavLink, useNavigate } from "react-router-dom";
import { House, Barbell, TrendUp, Trophy, Lightning, User, SignOut } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
  { to: "/feed", label: "Feed", icon: House, testid: "nav-feed" },
  { to: "/log", label: "Log", icon: Barbell, testid: "nav-log" },
  { to: "/prs", label: "PRs", icon: TrendUp, testid: "nav-prs" },
  { to: "/leaderboard", label: "Ranks", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/coach", label: "AI", icon: Lightning, testid: "nav-coach" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Desktop sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden md:flex fixed left-0 top-0 h-screen w-64 flex-col border-r border-[#27272A] bg-[#0A0A0A] px-6 py-8"
      >
        <div className="flex items-center gap-2 mb-12">
          <div className="w-8 h-8 bg-[#FF3B30] flex items-center justify-center">
            <Barbell size={20} weight="bold" color="#0A0A0A" />
          </div>
          <span className="font-display text-3xl font-black tracking-tight">SPOTTER</span>
        </div>

        <nav className="flex flex-col gap-1 flex-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 border-l-2 transition-all ${
                  isActive
                    ? "border-[#FF3B30] bg-[#1E1E1E] text-white"
                    : "border-transparent text-zinc-400 hover:text-white hover:bg-[#1E1E1E]"
                }`
              }
            >
              <item.icon size={20} weight="bold" />
              <span className="font-display uppercase tracking-widest text-sm font-bold">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto pt-6 border-t border-[#27272A]">
          <NavLink
            to="/profile"
            data-testid="nav-profile"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 mb-2 ${
                isActive ? "bg-[#1E1E1E] text-white" : "text-zinc-400 hover:text-white"
              }`
            }
          >
            <User size={20} weight="bold" />
            <span className="font-medium text-sm truncate">{user?.name || user?.username}</span>
          </NavLink>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 text-zinc-500 hover:text-[#FF3B30] transition-colors w-full text-left"
          >
            <SignOut size={18} weight="bold" />
            <span className="text-xs uppercase tracking-widest font-semibold">Sign out</span>
          </button>
        </div>
      </aside>

      {/* Mobile top header */}
      <header className="md:hidden sticky top-0 z-20 bg-[#0A0A0A] border-b border-[#27272A] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-[#FF3B30] flex items-center justify-center">
            <Barbell size={16} weight="bold" color="#0A0A0A" />
          </div>
          <span className="font-display text-2xl font-black">SPOTTER</span>
        </div>
        <button data-testid="mobile-logout" onClick={handleLogout} className="text-zinc-500">
          <SignOut size={20} weight="bold" />
        </button>
      </header>

      {/* Main content */}
      <main className="md:ml-64 pb-24 md:pb-8 min-h-screen">
        <div className="max-w-4xl mx-auto p-4 md:p-8">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-20 bg-[#0A0A0A] border-t border-[#27272A] grid grid-cols-6"
      >
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`m-${item.testid}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-3 gap-1 border-t-2 ${
                isActive ? "border-[#FF3B30] text-white" : "border-transparent text-zinc-500"
              }`
            }
          >
            <item.icon size={20} weight={"bold"} />
            <span className="text-[10px] uppercase tracking-wider font-bold">{item.label}</span>
          </NavLink>
        ))}
        <NavLink
          to="/profile"
          data-testid="m-nav-profile"
          className={({ isActive }) =>
            `flex flex-col items-center justify-center py-3 gap-1 border-t-2 ${
              isActive ? "border-[#FF3B30] text-white" : "border-transparent text-zinc-500"
            }`
          }
        >
          <User size={20} weight="bold" />
          <span className="text-[10px] uppercase tracking-wider font-bold">Me</span>
        </NavLink>
      </nav>
    </div>
  );
}
